import { Alert } from '@mui/material';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { MangopayReceiptBatchEntry } from '@pflegenavi/service-provider-frontend/api';
import { useTranslation } from 'react-i18next';
import { requiresManualSubmissionReceiptState } from '@pflegenavi/shared/api';

interface ReceiptBatchUnpaidReceiptsWarningProps {
  unpaidReceiptBatchEntries: MangopayReceiptBatchEntry[];
  /**
   * Service providers only ever see whether a receipt is paid or not - the
   * institution's internal receipt states must not leak into their UI.
   */
  audience: 'nursing-home' | 'service-provider';
}

export const ReceiptBatchUnpaidReceiptsWarning: FC<
  ReceiptBatchUnpaidReceiptsWarningProps
> = ({ unpaidReceiptBatchEntries, audience }) => {
  const { t } = useTranslation();

  const count = useMemo(() => {
    return unpaidReceiptBatchEntries.length;
  }, [unpaidReceiptBatchEntries.length]);

  const messageKey = useMemo(() => {
    if (audience === 'service-provider') {
      return 'receipts.batch.payment.unpaid-warning-service-provider';
    }

    return unpaidReceiptBatchEntries.some((entry) =>
      requiresManualSubmissionReceiptState(entry.receipt_state)
    )
      ? 'receipts.batch.payment.unpaid-warning-manual-submission'
      : 'receipts.batch.payment.partial-payment-warning';
  }, [audience, unpaidReceiptBatchEntries]);

  return <Alert severity="warning">{t(messageKey, { count })}</Alert>;
};
